import {
	Component,
	OnInit,
	Input,
	Inject,
	Output,
	EventEmitter,
	OnChanges,
	OnDestroy,
	SimpleChanges,
	HostListener,
} from '@angular/core';
import { JwtHelper } from '../../../services/core/jwt-helper.service';
import { DataCarrierService } from '../../../services/core/data-carrier.service';
import { AuthService } from '../../../services/core/auth.service';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Environment } from '../../../config/environment';
import { DOCUMENT } from '@angular/common';
import { MessagingService } from '../../../services/messaging/messaging.service';
import { HealthFinding } from '../../../beans/health-summary/health-summary.module.bean';
import { TutorialComponent } from '../../tutorial/tutorial.component';
import { MatDialog } from '@angular/material/dialog';
import { TutorialService } from '../../../services/tutorial/tutorial.service';
import { ProfileRelatedInfoBean } from '../../../beans/account/account-module.bean';
import { tutorialDisabled } from '../../../constants/app.constants';
import { USER_LEVEL } from '../../../constants/user.level.constants';
import { APP_USER_PERMISSIONS } from '../../../constants/app-user-permissions.constants';
import { NgxPermissionsService } from 'ngx-permissions';
import { AppointmentsService } from '../../../services/scheduling/appointment/appointments.service';
import { AssessmentVersion } from 'src/app/services/health-history/health-history.types';
import { debounceTime, filter, skip, takeUntil } from 'rxjs/operators';
import { Subject, combineLatest, forkJoin } from 'rxjs';
import { AssessmentCheckService } from 'src/app/services/health-history/assessment-check.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ProfileService } from 'src/app/services/core/profile.service';
import { NavController, Platform } from '@ionic/angular';
import { HealthMentorService } from '../../../services/health-mentor/health-mentor.service';

/**
 * Sidebar Navigation Component
 */
@Component({
	selector: 'app-sidebar-navigation',
	templateUrl: './sidebar-navigation.component.html',
	styleUrls: ['./sidebar-navigation.component.scss'],
})
export class SidebarNavigationComponent implements OnInit, OnChanges, OnDestroy {
	/** sidebar state */
	@Input() isCollapsed: boolean = false;
	@Input() isOpen: boolean = false;

	/** Output events */
	@Output() toggleSidebar = new EventEmitter<void>();
	@Output() collapseSidebar = new EventEmitter<'toggle' | 'close'>();

	/** exam result */
	examResults: HealthFinding;
	/** user permission list */
	userPermission: Array<string> = [];
	/** USER Level constants */
	USER_LEVEL = USER_LEVEL;
	/** MODULE Level constants */
	MODULE_LEVEL = APP_USER_PERMISSIONS;
	/** denote if biometricEligible or not */
	biometricEligible: boolean;
	/** denote if the page loaded */
	isLoaded: boolean;
	/** denote auth flag, helps to show different headers */
	@Input() auth: boolean = false;
	/** helps to show wellness header */
	@Input() wellness: boolean = false;
	/** To hide headers if user is from HA page */
	@Input() healthHistory: boolean = false;
	/** To show or hide phone number on header */
	@Input() showPhone: boolean = false;
	/** sharing profile related info to other components */
	@Output() profileRelatedInfo: EventEmitter<ProfileRelatedInfoBean>;
	/** profile related details */
	public profileRelatedInfoBean: ProfileRelatedInfoBean;
	/** user name */
	username: string;
	/** denote is matched member */
	isMatchedMember: boolean = false;
	/** tutorialDisabledFlag */
	tutorialDisabledFlag = tutorialDisabled;
	/** current URL */
	routerUrl: string;
	/** redirect URL */
	redirectUrl: string;
	private static readonly NAV_KEY = 'sidebar_last_nav';
	private get lastMatchedNavUrl(): string {
		return localStorage.getItem(SidebarNavigationComponent.NAV_KEY) ?? '/dashboard';
	}
	private set lastMatchedNavUrl(val: string) {
		localStorage.setItem(SidebarNavigationComponent.NAV_KEY, val);
	}
	/** Observable */
	private _destroyed$: Subject<boolean> = new Subject<boolean>();
	private _sidebarCountsReady = false;
	/** isSimplified */
	@Input() isSimplified: boolean = false;
	/** Whether viewport is mobile/tablet (for overlay) */
	isMobileOrTablet: boolean = false;
	/** Total appointment count */
	appointmentCount: number;
	/** Whether My Health sub-list is expanded (open) */
	healthSectionExpanded: boolean = false;
	// Top navigation link titles (match header for i18n/consistency)
	navBookExam: string = 'Book My Exam';
	navHome: string = 'Home';
	navMyHealth: string = 'My Health';
	navMyHealthAndData: string = 'My Health';
	navPersonalizedPlans: string = 'Personalized Plans';
	navMyMentors: string = 'Health Coach';
	navMyAppointments: string = 'My Appointments';
	navVisits: string = 'Visits';
	navCareTeam: string = 'Care Team';
	navProfile: string = 'Profile';
	navMessages: string = 'Messages';
	navHelp: string = 'Contact Us';
	navTour: string = 'Portal Tour';
	navMore: string = 'More';
	navSignOut: string = 'Sign Out';
	navSignIn: string = 'Sign In';
	navPhoneNumber: string;
	// Sub-nav under My Health
	navHealthRecords: string = 'Health Records';
	navVaccineRecords: string = 'Vaccine Records';
	navReferrals: string = 'Referrals';
	navHealthAssessment: string = 'Health Assessment';
	@Input() hideLinks: boolean = false;
	hideMyMentorNavLink: boolean = false;

	constructor(
		@Inject(DOCUMENT) private document: Document,
		public appointmentService: AppointmentsService,
		private assessmentCheckService: AssessmentCheckService,
		private authService: AuthService,
		private dataCarrierService: DataCarrierService,
		private dialog: MatDialog,
		private environment: Environment,
		private jwtService: JwtHelper,
		private permissionsService: NgxPermissionsService,
		private router: Router,
		private tutorialService: TutorialService,
		public messagingService: MessagingService,
		private spinner: SpinnerComponent,
		public profileService: ProfileService,
		private activatedRoute: ActivatedRoute,
		private navController: NavController,
		public platform: Platform,
		private healthMentorService: HealthMentorService
	) {
		this.username = this.jwtService.getUsername();
		this.profileRelatedInfoBean = new ProfileRelatedInfoBean();
		this.profileRelatedInfo = new EventEmitter<ProfileRelatedInfoBean>();
		// handling maintenance page
		if (this.environment.getEnvironment().UNDER_MAINTENANCE === 'true') {
			this.router.navigate(['maintenance']);
		}
		this.biometricEligible = true;
		this.isLoaded = false;
		this.routerUrl = router.url;
		this.redirectUrl = '/scheduling/create/appointment/v3';
		// Pre-expand Records section on first render to avoid visual flash
		this.healthSectionExpanded = this.isHealthSectionActive();
	}

	/** On initialization */
	ngOnInit() {
		this.navPhoneNumber = this.navPhoneNumber ?? '888.672.8172';
		this.checkViewport();

		if (!this.isSimplified && this.auth) {
			// Pre-populate isMatchedMember from the DataCarrierService cache
			const cachedProfile = this.dataCarrierService.getChannel().getValue().profile;
			if (cachedProfile?.profileInfo) {
				this.isMatchedMember = cachedProfile.profileInfo.userMatched;
			}
			localStorage.setItem('isMatchedUser', 'true');
			this.fetchProfileInfo();
		}

		combineLatest([
			this.appointmentService.getUpcomingAppointmentsCount(),
			this.healthMentorService.getUpcomingCoachingCount(),
		])
			.pipe(
				skip(1),
				filter(() => this._sidebarCountsReady),
				debounceTime(0),
				takeUntil(this._destroyed$)
			)
			.subscribe(([visits, coaching]) => {
				this.appointmentCount = (visits || 0) + (coaching || 0);
			});

		// Open sub-list when on /results, collapse when leaving
		this.healthSectionExpanded = this.isHealthSectionActive();
		this.lastMatchedNavUrl = this.resolveMatchedNavUrl(this.router.url) ?? '/dashboard';
		this.router.events.pipe(takeUntil(this._destroyed$)).subscribe(event => {
			this.healthSectionExpanded = this.isHealthSectionActive();
			if (event instanceof NavigationEnd) {
				const matched = this.resolveMatchedNavUrl(event.urlAfterRedirects);
				if (matched) this.lastMatchedNavUrl = matched;
				if (this.auth && !this.isSimplified) {
					this.appointmentCount = 0;
					this.fetchProfileInfo();
				}
			}
		});
	}

	/*** Life cycle hook - On Changes */
	ngOnChanges(changes: SimpleChanges) {
		const biometricValue = this.dataCarrierService.getChannel().getValue().biometricEligible;
		if (biometricValue !== undefined && biometricValue !== null) {
			this.biometricEligible = biometricValue;
		}
		// fetch profile info if auth changes
		if (changes['auth']) {
			this.fetchProfileInfo();
		}
	}

	fetchProfileInfo() {
		const epmsPID = this.jwtService.getEpmsId();
		if (this.auth && epmsPID) {
			this.getProfileInfo();
			this._getDataCarrierService();
		} else {
			this.profileRelatedInfoBean.noMatch = true;
			this.profileRelatedInfo.emit(this.profileRelatedInfoBean);
		}
	}

	/**
	 * Subscribes to the DataCarrierService to get the latest values
	 */
	private _getDataCarrierService(): void {
		this.dataCarrierService
			.getChannel()
			.pipe(takeUntil(this._destroyed$))
			.subscribe(data => {
				const canLoadData = this._permissionCheck();
				if (canLoadData === true) {
					return this._loadBackgroundAsyncCalls(data);
				}
				return;
			});
	}

	/**
	 * Checks if the user has the correct permissions to load the data
	 */
	private _permissionCheck(): boolean {
		if (this.userPermission.length > 0) {
			const { CLIENT_BIOMETRIC_NOT_ELIGIBLE, UNMATCHED } = USER_LEVEL;
			const hubbleNotEligibleUser = this.userPermission.includes(CLIENT_BIOMETRIC_NOT_ELIGIBLE);
			const unmatchedUser = this.userPermission.includes(UNMATCHED);
			const invalidPerms = hubbleNotEligibleUser || unmatchedUser;
			return invalidPerms === false;
		}
		return false;
	}

	/**
	 * Loads background async calls based on data carrier service
	 */
	private _loadBackgroundAsyncCalls(data: any): void {
		const { assessmentVersionFormat }: { assessmentVersionFormat: boolean } = data;
		if (assessmentVersionFormat === undefined || assessmentVersionFormat === null) {
			this._getAssessmentVersion();
		}
	}

	/**
	 * Retrieves the AssessmentVersion for the user
	 */
	private _getAssessmentVersion(): void {
		this.assessmentCheckService
			.getAssessmentVersion()
			.pipe(takeUntil(this._destroyed$))
			.subscribe(
				(assessmentVersion: AssessmentVersion) => this._assessmentFormatCheck(assessmentVersion),
				(err: Response) => {
					console.error(`[${SidebarNavigationComponent.name}][${this._getAssessmentVersion.name}]`, err);
				}
			);
	}

	/**
	 * Checks for an old format of the health assessment
	 */
	private _assessmentFormatCheck(assessmentVersion: AssessmentVersion): void {
		const { oldFormat } = assessmentVersion;
		this.dataCarrierService.pushValue('assessmentVersionFormat', oldFormat);
		if (oldFormat === true) {
			const epmsPID = this.jwtService.getEpmsId();
			this.dataCarrierService.pushValue('assessmentPdf', {
				id: 0,
				documentName: 'Health_Assessment',
				epmsPID: epmsPID,
			});
		}
	}

	/** get profile information from service */
	getProfileInfo() {
		forkJoin({
			profile: this.profileService.getProfileInfo(),
			coaching: this.healthMentorService.fetchUpcomingCoachingCount(),
		}).subscribe({
			next: ({ profile, coaching }) => {
				this.profileRelatedInfoBean = profile;
				this.setProfileInfo();
				this.dataCarrierService.pushValue('profile', this.profileRelatedInfoBean);
				this.appointmentService.setUpcomingAppointmentCount(profile.confirmedAppointmentCount);
				this.healthMentorService.setUpcomingCoachingCount(coaching);
				this._sidebarCountsReady = true;
			},
			error: (_err: any) => {
				this._sidebarCountsReady = true;
				this.spinner.stopLoader();
			},
		});
	}

	setBasicContext() {
		this.profileService.setFirstName(this.profileRelatedInfoBean.profileInfo.firstName);
		this.profileService.setLastName(this.profileRelatedInfoBean.profileInfo.lastName);
		this.profileService.setGender(this.profileRelatedInfoBean.profileInfo.gender);
		this.isMatchedMember = this.profileRelatedInfoBean.profileInfo.userMatched;
		localStorage.setItem('isMatchedUser', '' + this.isMatchedMember);
		if (this.userPermission.length === 0) {
			this.setPermission();
		}
		this.profileRelatedInfo.emit(this.profileRelatedInfoBean);
	}

	setProfileInfo() {
		this.setBasicContext();
		this.messagingService.setCurrentMessageCount({
			inbox: this.profileRelatedInfoBean.unreadMessageCount,
		});
	}

	/**
	 * logout the user from application
	 */
	logout() {
		this.authService.logoutUser();
		this.navController.navigateRoot(['/login'], { queryParams: { clear: true } });
	}

	/** Handling tutorial modal */
	openTutorialModal() {
		const dialogRef = this.dialog.open(TutorialComponent, {
			disableClose: true,
			backdropClass: 'tutorial-backdrop',
		});

		dialogRef.afterClosed().subscribe(result => {
			const postBody = { status: result };
			this.tutorialService.postStatus(postBody);
		});
	}

	/** Setting user permission based on flags */
	setPermission() {
		this.isLoaded = true;
		const { profileRelatedInfoBean } = this;
		const { profileInfo } = profileRelatedInfoBean;
		const { userMatched, client, eligibleForPE } = profileInfo;
		const { CLIENT_BIOMETRIC_ELIGIBLE, CLIENT_BIOMETRIC_NOT_ELIGIBLE, MATCHED, UNMATCHED, CLIENT_HIDE_HM } = USER_LEVEL;

		if (userMatched) {
			if (client.isSsn) {
				this.dataCarrierService.pushValue('biometricUser', true);
				this.dataCarrierService.pushValue('eligibleForPE', eligibleForPE);
				if (eligibleForPE) {
					this.biometricEligible = true;
					this.dataCarrierService.pushValue('biometricEligible', this.biometricEligible);
					this.userPermission.push(CLIENT_BIOMETRIC_ELIGIBLE);
				} else {
					this.biometricEligible = false;
					this.dataCarrierService.pushValue('biometricEligible', this.biometricEligible);
					this.userPermission.push(CLIENT_BIOMETRIC_NOT_ELIGIBLE);
				}
			} else {
				this.biometricEligible = true;
				this.userPermission.push(MATCHED);
				if (client.hideMentor) {
					this.userPermission.push(CLIENT_HIDE_HM);
					this.hideMyMentorNavLink = true;
				}
			}
		} else {
			this.biometricEligible = true;
			this.userPermission.push(UNMATCHED);
		}

		// keep the user level permissions in sync with app level permissions
		const appLevelPermissions = this.authService.getAppPermissionsForLoggedInUser() || [];
		this.permissionsService.loadPermissions([...this.userPermission, ...appLevelPermissions]);
	}

	@HostListener('window:resize')
	onResize(): void {
		const wasMobile = this.isMobileOrTablet;

		this.checkViewport();

		// Only trigger when switching Desktop → Mobile
		if (!wasMobile && this.isMobileOrTablet) {
			this.collapseSidebar.emit('close');
		}
	}

	/** Check if viewport is mobile/tablet width */
	checkViewport(): void {
		this.isMobileOrTablet = typeof window !== 'undefined' && window.innerWidth < 992;
	}

	ngOnDestroy(): void {
		this._destroyed$.next(true);
		this._destroyed$.unsubscribe();
	}

	/**
	 * Toggle sidebar collapsed/expanded state
	 */
	onToggleSidebar() {
		this.collapseSidebar.emit('toggle');
	}

	/**
	 * Whether the current route is under My Health (for showing sub-nav).
	 */
	isHealthSectionActive(): boolean {
		const url = this.router.url.split('?')[0];
		return (
			url === '/dashboard/my-health-dashboard' ||
			url.startsWith('/dashboard/my-health-dashboard/') ||
			url === '/results' ||
			url.startsWith('/results/') ||
			url === '/vaxstatus' ||
			url.startsWith('/vaxstatus/') ||
			url === '/wellness/referral' ||
			url.startsWith('/wellness/referral/') ||
			url === '/assessment' ||
			url.startsWith('/assessment/')
		);
	}

	/**
	 * Home is active only when the effective nav key is /dashboard AND My Health is not expanded.
	 */
	isHomeActive(): boolean {
		return this.activeNavKey === '/dashboard' && !this.healthSectionExpanded;
	}

	/** My Health is active when the effective nav key is /health-section. */
	isMyHealthActive(): boolean {
		return this.activeNavKey === '/health-section';
	}

	/** Visits is active when the effective active nav key resolves to /scheduling */
	isVisitsActive(): boolean {
		return this.activeNavKey === '/scheduling';
	}

	/** Health Coaching is active when the effective nav key resolves to /health/mentor or ends with coaching */
	isHealthCoachingActive(): boolean {
		const key = this.activeNavKey;
		return key === '/health/mentor' || key.endsWith('coaching');
	}

	/** Care Team is active when the effective nav key resolves to /care-team */
	isCareTeamActive(): boolean {
		return this.activeNavKey === '/care-team';
	}

	/** Messages is active when the effective nav key resolves to /messaging */
	isMessagesActive(): boolean {
		return this.activeNavKey === '/messaging';
	}

	/** Profile is active when the effective nav key resolves to /account */
	isProfileActive(): boolean {
		return this.activeNavKey === '/account';
	}

	/** Contact Us is active when the effective nav key resolves to /faq */
	isContactUsActive(): boolean {
		return this.activeNavKey === '/faq';
	}

	/**
	 * The effective active nav key: resolves the current URL to a nav key,
	 * falling back to the last matched key when the URL is an orphan route.
	 */
	private get activeNavKey(): string {
		return this.resolveMatchedNavUrl(this.router.url) ?? this.lastMatchedNavUrl;
	}

	/**
	 * Maps a URL to the nav key that owns it. Returns null for orphan routes
	 * (e.g. /scheduling/create/...) so the caller falls back to lastMatchedNavUrl.
	 */
	private resolveMatchedNavUrl(raw: string): string | null {
		const url = (raw || '').split('?')[0];
		if (url === '/dashboard') return '/dashboard';
		if (url === '/scheduling/appointments' || url === '/scheduling/appointments/') return '/scheduling';
		if (url.startsWith('/health/mentor') || url.endsWith('/coaching') || url.includes('/coaching/'))
			return '/health/mentor';
		if (url.startsWith('/care-team')) return '/care-team';
		if (url.startsWith('/messaging/')) return '/messaging';
		if (url.startsWith('/account')) return '/account';
		if (url.startsWith('/faq')) return '/faq';
		if (
			url.startsWith('/results') ||
			url.startsWith('/vaxstatus') ||
			url.startsWith('/wellness/referral') ||
			url.startsWith('/assessment') ||
			url.startsWith('/dashboard/my-health-dashboard')
		)
			return '/health-section';
		return null;
	}

	/**
	 * Toggle the Records sub-list (expand/collapse). Does not navigate.
	 */
	toggleHealthSection() {
		const onHealthRoute = this.isHealthSectionActive();
		if (onHealthRoute) {
			this.healthSectionExpanded = !this.healthSectionExpanded;
		} else {
			this.healthSectionExpanded = true;
			this.navigateOnly('/dashboard/my-health-dashboard');
		}
	}

	/**
	 * Navigate to the Records landing page and ensure sub-list is expanded.
	 */
	navigateToRecords() {
		this.healthSectionExpanded = true;
		this.navigateOnly('/dashboard/my-health-dashboard');
	}

	/**
	 * Whether to show Health Coaching nav item (HM or PW permission, and not CLIENT_HIDE_HM).
	 * Uses permission store so it updates when permissions load (e.g. from ID token).
	 */
	canShowHealthCoaching(): boolean {
		const p = this.permissionsService.getPermissions() || {};
		const hasAccess = !!(p[this.MODULE_LEVEL.HM] || p[this.MODULE_LEVEL.PW]);
		const hiddenByClient = !!p[this.USER_LEVEL.CLIENT_HIDE_HM];
		return hasAccess && !hiddenByClient;
	}

	/**
	 * Navigate to route and close sidebar on mobile
	 */
	navigateAndCloseSidebar(route: string, queryParams?: any) {
		if (this.platform.is('mobile')) {
			this.onToggleSidebar();
		}
		if (queryParams) {
			this.router.navigate([route], { queryParams });
		} else {
			this.router.navigate([route]);
		}
	}

	/**
	 * Navigate to route without changing sidebar state.
	 */
	navigateOnly(route: string, queryParams?: any) {
		if (queryParams) {
			this.router.navigate([route], { queryParams });
		} else {
			this.router.navigate([route]);
		}
	}
}
